// 🔧 firestore_read_provider.js
// ✅ Purpose: Read-only Firestore provider for the Communication Engine
// ✅ Features:
// - Reads published runtime bundles from /content_bundles
// - Reads editable content items from /content_items
// - Supports filtered content item queries for Communication Studio
// - Supports future manifest reads
// - Keeps Firestore implementation out of services, hooks, and pages

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";

/**
 * Reads one published Content Bundle.
 */
export async function getPublishedBundle(bundleKey) {
  console.log(
    "[Communication Engine] Reading published bundle:",
    bundleKey
  );

  if (!bundleKey) {
    return null;
  }

  const documentRef = doc(
    db,
    "content_bundles",
    bundleKey
  );

  const snapshot = await getDoc(documentRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  };
}

/**
 * Reads one editable Content Item.
 */
export async function getContentItem(contentKey) {
  console.log(
    "[Communication Engine] Reading content item:",
    contentKey
  );

  if (!contentKey) {
    return null;
  }

  const documentRef = doc(
    db,
    "content_items",
    contentKey
  );

  const snapshot = await getDoc(documentRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  };
}

/**
 * Queries editable Content Items.
 */
export async function queryContentItems(filters = {}) {
  console.log(
    "[Communication Engine] Querying content items:",
    filters
  );

  try {
    /**
     * Keep Firestore query intentionally simple.
     *
     * Communication Studio usually loads a small number of content items,
     * so we query by area only and apply the remaining filters in JS.
     * This avoids composite index requirements during local/dev/Playwright.
     */
    const itemsRef = collection(db, "content_items");

    const itemsQuery = filters.area
      ? query(
          itemsRef,
          where("area", "==", filters.area)
        )
      : query(itemsRef);

    const snapshot = await getDocs(itemsQuery);

    return snapshot.docs
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }))
      .filter((item) => {
        if (filters.status && item.status !== filters.status) {
          return false;
        }

        if (filters.scopeId && item.scopeId !== filters.scopeId) {
          return false;
        }

        if (filters.locale && item.locale !== filters.locale) {
          return false;
        }

        if (filters.profile && item.profile !== filters.profile) {
          return false;
        }

        if (
          filters.organizationId !== undefined &&
          item.organizationId !== filters.organizationId
        ) {
          return false;
        }

        if (
          filters.eventId !== undefined &&
          item.eventId !== filters.eventId
        ) {
          return false;
        }

        if (filters.type && item.type !== filters.type) {
          return false;
        }

        return true;
      });
  } catch (error) {
    console.error(
      "[Communication Engine] queryContentItems failed:",
      error
    );

    throw error;
  }
}

/**
 * Backward-compatible helper used by Bundle Service.
 */
export async function getPublishedContentItemsForArea({
  area,
  scopeId,
  locale,
  profile,
} = {}) {
  return queryContentItems({
    area,
    scopeId,
    locale,
    profile,
    status: "published",
  });
}

/**
 * Reads one content manifest.
 */
export async function getContentManifest(manifestKey) {
  console.log(
    "[Communication Engine] Reading content manifest:",
    manifestKey
  );

  if (!manifestKey) {
    return null;
  }

  const documentRef = doc(
    db,
    "content_manifests",
    manifestKey
  );

  const snapshot = await getDoc(documentRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  };
}